import { useState } from 'react';
import { LogoIcon } from '../Icons';
import { LegalModal } from './LegalModal';
import { Link } from 'react-router-dom';
import { IconBox } from '@/components/ui';

const legalContent = {
  privacy: {
    title: 'Privacy Policy',
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          Last Updated: {new Date().toLocaleDateString()}
        </p>

        <h2 className="text-xl font-semibold">Quick Summary</h2>
        <p>
          ChytBot respects your privacy. We collect minimal data needed to
          provide our AI chatbot service and never sell your personal
          information.
        </p>

        <h2 className="text-xl font-semibold">What We Collect</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Usage Data:</strong> Analytics to improve performance
          </li>
          <li>
            <strong>Cookies:</strong> For authentication and preferences
          </li>
        </ul>

        <h2 className="text-xl font-semibold">Contact</h2>
        <p>For privacy concerns: privacy@chytbot.com</p>
      </div>
    ),
  },
  terms: {
    title: 'Terms of Service',
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          Last Updated: {new Date().toLocaleDateString()}
        </p>

        <h2 className="text-xl font-semibold">1. Acceptance of Terms</h2>
        <p>
          By using ChytBot, you agree to these terms. If you disagree, please
          don't use our service.
        </p>

        <h2 className="text-xl font-semibold">2. Service Description</h2>
        <p>
          ChytBot provides AI-powered customer service chatbots for businesses.
          We offer:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>24/7 automated customer support</li>
          <li>Customizable AI responses</li>
          <li>Integration with your website</li>
          <li>Analytics and reporting</li>
        </ul>

        <h2 className="text-xl font-semibold">Contact</h2>
        <p>For questions: legal@chytbot.com</p>
      </div>
    ),
  },
  cookies: {
    title: 'Cookie Policy',
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          Last Updated: {new Date().toLocaleDateString()}
        </p>

        <h2 className="text-xl font-semibold">What Are Cookies?</h2>
        <p>
          Cookies are small files stored on your device that help us provide a
          better experience.
        </p>

        <h2 className="text-xl font-semibold">How We Use Cookies</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold">Essential Cookies (Required)</h3>
            <ul className="list-disc pl-6">
              <li>Keep you logged in</li>
              <li>Remember your preferences</li>
              <li>Ensure security</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold">Analytics Cookies (Optional)</h3>
            <ul className="list-disc pl-6">
              <li>Understand how you use ChytBot</li>
              <li>Improve our service</li>
              <li>Track performance</li>
            </ul>
          </div>
        </div>

        <h2 className="text-xl font-semibold">Contact</h2>
        <p>Questions about cookies: support@chytbot.com</p>
      </div>
    ),
  },
};

export const Footer = () => {
  const [activeModal, setActiveModal] = useState<
    'privacy' | 'terms' | 'cookies' | null
  >(null);

  return (
    <footer id="footer" className="relative border-t border-border bg-background overflow-hidden">
      <div className="container py-16 relative">

        {/* Zone 1: Brand row */}
        <div className="mb-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-3">
            <IconBox size="lg" radius="lg" className="border border-primary/20">
              <LogoIcon />
            </IconBox>
            <span className="font-bold text-foreground text-lg">ChytBot</span>
          </Link>
          <p className="text-sm text-muted-foreground max-w-xs">
            The AI chatbot for customer service — conversational AI trained on your docs, live online in minutes.
          </p>
        </div>

        {/* Zone 2: 4-column link grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">

          {/* Product Column */}
          <div>
            <h3 className="font-bold mb-6 text-foreground uppercase tracking-widest text-xs">Product</h3>
            <ul className="space-y-1">
              <li>
                <a href="/#features" className="text-sm text-muted-foreground hover:text-primary transition-colors py-2 min-h-[44px] flex items-center">
                  Features
                </a>
              </li>
              <li>
                <Link to="/pricing" className="text-sm text-muted-foreground hover:text-primary transition-colors py-2 min-h-[44px] flex items-center">
                  Pricing
                </Link>
              </li>
              <li>
                <a href="https://docs.chytbot.com" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary transition-colors py-2 min-h-[44px] flex items-center">
                  Docs
                </a>
              </li>
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h3 className="font-bold mb-6 text-foreground uppercase tracking-widest text-xs">Resources</h3>
            <ul className="space-y-1">
              <li>
                <span title="Coming soon" className="text-sm text-muted-foreground cursor-default opacity-50 py-2 min-h-[44px] flex items-center">
                  Blog
                </span>
              </li>
              <li>
                <span title="Coming soon" className="text-sm text-muted-foreground cursor-default opacity-50 py-2 min-h-[44px] flex items-center">
                  Changelog
                </span>
              </li>
              <li>
                <span title="Coming soon" className="text-sm text-muted-foreground cursor-default opacity-50 py-2 min-h-[44px] flex items-center">
                  Status
                </span>
              </li>
            </ul>
          </div>

          {/* Legal Column — per D-07 */}
          <div>
            <h3 className="font-bold mb-6 text-foreground uppercase tracking-widest text-xs">Legal</h3>
            <ul className="space-y-1">
              <li>
                <button
                  onClick={() => setActiveModal('privacy')}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors py-2 min-h-[44px] flex items-center text-left w-full"
                >
                  Privacy
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveModal('terms')}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors py-2 min-h-[44px] flex items-center text-left w-full"
                >
                  Terms
                </button>
              </li>
              <li>
                <Link to="/security-privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors py-2 min-h-[44px] flex items-center">
                  Security
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="font-bold mb-6 text-foreground uppercase tracking-widest text-xs">Company</h3>
            <ul className="space-y-1">
              <li>
                <span title="Coming soon" className="text-sm text-muted-foreground cursor-default opacity-50 py-2 min-h-[44px] flex items-center">
                  About
                </span>
              </li>
              <li>
                <a href="mailto:hello@chytbot.com" className="text-sm text-muted-foreground hover:text-primary transition-colors py-2 min-h-[44px] flex items-center">
                  Contact
                </a>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-6 border-t border-border">
          <p className="text-sm text-muted-foreground">
            © 2026 ChytBot. All rights reserved.
          </p>
        </div>
      </div>

      {/* Legal Modals */}
      <LegalModal
        isOpen={activeModal === 'privacy'}
        onClose={() => setActiveModal(null)}
        title={legalContent.privacy.title}
        content={legalContent.privacy.content}
      />
      <LegalModal
        isOpen={activeModal === 'terms'}
        onClose={() => setActiveModal(null)}
        title={legalContent.terms.title}
        content={legalContent.terms.content}
      />
      <LegalModal
        isOpen={activeModal === 'cookies'}
        onClose={() => setActiveModal(null)}
        title={legalContent.cookies.title}
        content={legalContent.cookies.content}
      />
    </footer>
  );
};
